/**
 * useWeblabsData — data hook for the Weblabs dashboard (server-side aggregates).
 *
 * The dashboard never pulls raw rows. This hook runs small, partition-pruned
 * aggregate queries against `sandbox.finn_preprocessed_data_weblabs` via the
 * shared `getWeblabView` raw-SQL path:
 *   - chart:   one row per day (pos / neg + annualized split)
 *   - KPIs:    net, net annualized, top +/- weblab
 *   - options: distinct marketplace / activation-state / weblab-type for the range
 * Results are tiny regardless of weblab count or range length, so nothing is
 * ever truncated. Per-weblab detail (day breakdown, drill-in) is fetched ON
 * DEMAND via the returned `fetchBreakdown` / `fetchDrill` callbacks.
 *
 * ALL filters (dates, marketplace, activation state, weblab type, weblab
 * start/launch-date range) and the SP/SB metric are pushed into the SQL, so
 * changing any of them re-queries (a brief load) rather than filtering stale
 * client rows.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchWeblabView,
  fetchDistinct,
  extractGraphQLError,
  buildChartQuery,
  buildKpiSummaryQuery,
  buildKpiTopQuery,
  buildBreakdownQuery,
  buildDrillQuery,
  WEBLABS_DATABASE,
  type WeblabsQueryFilters,
} from "./weblabsService";
import { toNum, type WeblabMetric } from "./weblabsChart";

/** All query inputs: the server-side filters plus the chosen metric. */
export interface WeblabsQueryParams extends WeblabsQueryFilters {
  metric: WeblabMetric;
}

/** One point on the chart (one day): impact + annualized, split by sign. */
export interface ChartPoint {
  day: string;
  pos: number;
  neg: number;
  net: number;
  annPos: number;
  annNeg: number;
  annNet: number;
}

export interface TopWeblab {
  weblab: string;
  total: number;
  ann: number | null;
}

export interface WeblabsKpis {
  net: number;
  annNet: number;
  topPositive: TopWeblab | null;
  topNegative: TopWeblab | null;
}

export interface WeblabsOptions {
  countries: string[];
  activationStates: string[];
  weblabTypes: string[];
}

/** One row of a day/bucket breakdown. */
export interface BreakdownEntry {
  weblab: string;
  value: number;
  ann: number | null;
}

/** A weblab's attributes for the drill-in header. */
export interface DrillInfo {
  weblab: string;
  weblab_type: string | null;
  primary_owner: string | null;
  activation_state: string | null;
  weblab_start_date: string | null;
  weblab_launch_date: string | null;
  /** Annualized impact for the CURRENT metric. */
  annualized: number | null;
}

export interface DrillResult {
  series: { day: string; value: number }[];
  info: DrillInfo | null;
}

export interface WeblabsData {
  chart: ChartPoint[];
  kpis: WeblabsKpis;
  options: WeblabsOptions;
}

export interface UseWeblabsDataResult {
  data: WeblabsData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  /** Per-weblab breakdown for a day/bucket range (top-N by |impact|). */
  fetchBreakdown: (
    bucketStart: string,
    bucketEnd: string
  ) => Promise<BreakdownEntry[]>;
  /** Daily series + attributes for one weblab (drill-in). */
  fetchDrill: (weblab: string) => Promise<DrillResult>;
}

const EMPTY_KPIS: WeblabsKpis = {
  net: 0,
  annNet: 0,
  topPositive: null,
  topNegative: null,
};
const EMPTY_OPTIONS: WeblabsOptions = {
  countries: [],
  activationStates: [],
  weblabTypes: [],
};

function n(v: unknown): number {
  return toNum(v) ?? 0;
}

export function useWeblabsData(
  params: WeblabsQueryParams
): UseWeblabsDataResult {
  const [chart, setChart] = useState<ChartPoint[]>([]);
  const [kpis, setKpis] = useState<WeblabsKpis>(EMPTY_KPIS);
  const [options, setOptions] = useState<WeblabsOptions>(EMPTY_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aggAbort = useRef<AbortController | null>(null);

  const {
    startDate,
    endDate,
    country,
    activationState,
    weblabType,
    runStart,
    runEnd,
    metric,
  } = params;
  const ready = Boolean(startDate && endDate);

  // Filters object reused by every query (excludes metric, which is separate).
  const filters: WeblabsQueryFilters = {
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    country,
    activationState,
    weblabType,
    runStart,
    runEnd,
  };

  // --- Chart + KPI aggregates (react to ALL params) -----------------------
  const fetchAggregates = useCallback(async () => {
    if (!ready || !startDate || !endDate) return;
    aggAbort.current?.abort();
    const controller = new AbortController();
    aggAbort.current = controller;
    setIsLoading(true);
    setError(null);
    try {
      const [chartRes, kpiRes, topRes] = await Promise.all([
        fetchWeblabView({
          query: buildChartQuery(filters, metric),
          database: WEBLABS_DATABASE,
          max_results: 5000,
        }),
        fetchWeblabView({
          query: buildKpiSummaryQuery(filters, metric),
          database: WEBLABS_DATABASE,
          max_results: 1,
        }),
        fetchWeblabView({
          query: buildKpiTopQuery(filters, metric),
          database: WEBLABS_DATABASE,
          max_results: 2,
        }),
      ]);
      if (controller.signal.aborted) return;

      const chartPoints: ChartPoint[] = (chartRes.rows ?? []).map((r: any) => {
        const pos = n(r.pos);
        const neg = n(r.neg);
        const annPos = n(r.ann_pos);
        const annNeg = n(r.ann_neg);
        return {
          day: String(r.day),
          pos,
          neg,
          net: pos + neg,
          annPos,
          annNeg,
          annNet: annPos + annNeg,
        };
      });

      const summary = kpiRes.rows?.[0] ?? {};
      const topRows = topRes.rows ?? [];
      const toTop = (kind: string): TopWeblab | null => {
        const row = topRows.find((r: any) => r.kind === kind);
        if (!row) return null;
        return {
          weblab: String(row.weblab),
          total: n(row.impact_total),
          ann: toNum(row.ann),
        };
      };

      setChart(chartPoints);
      setKpis({
        net: n(summary.net),
        annNet: n(summary.ann_net),
        topPositive: toTop("pos"),
        topNegative: toTop("neg"),
      });
    } catch (err: any) {
      if (controller.signal.aborted) return;
      console.error("useWeblabsData aggregates failed:", err);
      setError(extractGraphQLError(err) || "Failed to load Weblabs data");
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    startDate,
    endDate,
    country,
    activationState,
    weblabType,
    runStart,
    runEnd,
    metric,
  ]);

  useEffect(() => {
    fetchAggregates();
    return () => aggAbort.current?.abort();
  }, [fetchAggregates]);

  // --- Filter options (react only to the DATE RANGE) ----------------------
  useEffect(() => {
    if (!ready || !startDate || !endDate) return;
    let cancelled = false;
    (async () => {
      try {
        const [countries, activationStates, weblabTypes] = await Promise.all([
          fetchDistinct(startDate, endDate, "country"),
          fetchDistinct(startDate, endDate, "activation_state"),
          fetchDistinct(startDate, endDate, "weblab_type"),
        ]);
        if (!cancelled)
          setOptions({ countries, activationStates, weblabTypes });
      } catch (err) {
        // Options are non-critical; leave whatever we have.
        console.error("useWeblabsData options failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, startDate, endDate]);

  // --- On-demand detail fetchers ------------------------------------------
  const fetchBreakdown = useCallback(
    async (bucketStart: string, bucketEnd: string) => {
      const res = await fetchWeblabView({
        query: buildBreakdownQuery(filters, metric, bucketStart, bucketEnd, 50),
        database: WEBLABS_DATABASE,
        max_results: 100,
      });
      // The query slices the top-N by |impact| (to catch both directions), then
      // we present them signed-descending: winners first, losers last.
      return (res.rows ?? [])
        .map((r: any) => ({
          weblab: String(r.weblab),
          value: n(r.value),
          ann: toNum(r.ann),
        }))
        .sort((a, b) => b.value - a.value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      country,
      activationState,
      weblabType,
      runStart,
      runEnd,
      metric,
    ]
  );

  const fetchDrill = useCallback(
    async (weblab: string): Promise<DrillResult> => {
      const res = await fetchWeblabView({
        query: buildDrillQuery(filters, metric, weblab),
        database: WEBLABS_DATABASE,
        max_results: 5000,
      });
      const rows = res.rows ?? [];
      const series = rows.map((r: any) => ({
        day: String(r.day),
        value: n(r.value),
      }));
      const annCol =
        metric === "impact_sp_rev_day"
          ? "sp_rev_annualized"
          : "sb_rev_annualized";
      const first: any = rows[0];
      const info: DrillInfo | null = first
        ? {
            weblab,
            weblab_type: first.weblab_type ?? null,
            primary_owner: first.primary_owner ?? null,
            activation_state: first.activation_state ?? null,
            weblab_start_date: first.weblab_start_date ?? null,
            weblab_launch_date: first.weblab_launch_date ?? null,
            annualized: toNum(first[annCol]),
          }
        : null;
      return { series, info };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      country,
      activationState,
      weblabType,
      runStart,
      runEnd,
      metric,
    ]
  );

  return {
    data: ready ? { chart, kpis, options } : null,
    isLoading: isLoading || !ready,
    error,
    refetch: fetchAggregates,
    fetchBreakdown,
    fetchDrill,
  };
}
